import type {TreeNode} from '@hm/fundamental'
import {mdiHome, mdiHomeOutline} from '@mdi/js'
import {nanoid} from 'nanoid'

import {FeatureMenuEntry, MenuNodeType, UpdateMenuInputModel} from '../../../api/types/featuresMenu'

export type EditMode = 'off' | 'edit' | 'setHomePage'

export interface FeatureMenuNode {
  id: string
  type: MenuNodeType
  name: string
  featureId: number | null
}

export const toNodeId = (item: FeatureMenuEntry): string => {
  if (item.type === MenuNodeType.Folder && item.id === 0) {
    return `${item.type}-${item.name}`
  }

  return `${item.type}-${item.id}`
}

export const createDraftFolderNodeId = (): string => `${MenuNodeType.Folder}-temp-${nanoid()}`

export const DRAFT_NODE_PREFIX = 'draft'

export const createDraftFeatureNodeId = (featureId: number): string =>
  `${MenuNodeType.Feature}-${DRAFT_NODE_PREFIX}-${featureId}`

export const sortByDisplayOrder = (items: FeatureMenuEntry[]): FeatureMenuEntry[] =>
  [...items]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((item) =>
      item.type === MenuNodeType.Folder
        ? {...item, children: sortByDisplayOrder(item.children)}
        : item
    )

export const transformFeatureMenuToTreeNodes = (items: FeatureMenuEntry[]): TreeNode[] =>
  items.map(toTreeNode)

const toTreeNode = (item: FeatureMenuEntry): TreeNode => ({
  id: toNodeId(item),
  label: item.name,
  children: item.type === MenuNodeType.Folder ? item.children.map(toTreeNode) : undefined
})

export const buildNodeLookup = (items: FeatureMenuEntry[]): Map<string, FeatureMenuNode> => {
  const map = new Map<string, FeatureMenuNode>()
  const walk = (entries: FeatureMenuEntry[]) => {
    for (const item of entries) {
      map.set(toNodeId(item), {
        id: toNodeId(item),
        type: item.type,
        name: item.name,
        featureId: item.type === MenuNodeType.Feature ? item.featureId : null
      })
      if (item.type === MenuNodeType.Folder) {
        walk(item.children)
      }
    }
  }
  walk(items)
  return map
}

export interface ActiveFeatureLocation {
  nodeId: string
  ancestorFolderIds: string[]
}

/**
 * Locates the feature node matching the given feature name and returns its node id
 * together with the ids of all ancestor folders, so the active feature can be
 * highlighted and revealed in the tree (e.g. after a page refresh).
 */
export const findActiveFeatureLocation = (
  items: FeatureMenuEntry[],
  featureName: string
): ActiveFeatureLocation | undefined => {
  let result: ActiveFeatureLocation | undefined

  const walk = (entries: FeatureMenuEntry[], ancestorFolderIds: string[]): boolean => {
    for (const item of entries) {
      if (item.type === MenuNodeType.Feature) {
        if (item.name === featureName) {
          result = {nodeId: toNodeId(item), ancestorFolderIds}
          return true
        }
      } else if (walk(item.children, [...ancestorFolderIds, toNodeId(item)])) {
        return true
      }
    }
    return false
  }

  walk(items, [])
  return result
}

export const mapTreeNodesToUpdateModel = (
  nodes: TreeNode[],
  lookup: Map<string, FeatureMenuNode>,
  homePageNodeId: string | null
): UpdateMenuInputModel[] =>
  nodes.map((node, index) => {
    const meta = lookup.get(node.id)
    const isFolder = meta?.type === MenuNodeType.Folder || (!meta && node.children !== undefined)

    return {
      displayOrder: index + 1,
      type: isFolder ? MenuNodeType.Folder : MenuNodeType.Feature,
      name: meta?.type === MenuNodeType.Feature ? meta.name : node.label,
      children: node.children
        ? mapTreeNodesToUpdateModel(node.children, lookup, homePageNodeId)
        : null,
      featureId: meta?.featureId ?? null,
      isHomePage: isFolder ? null : node.id === homePageNodeId
    }
  })

export const applyDroppableTreeNodes = (
  nodes: TreeNode[],
  lookup: Map<string, FeatureMenuNode>
): TreeNode[] =>
  nodes.map((node) => {
    const meta = lookup.get(node.id)
    const isFolder = meta?.type === MenuNodeType.Folder || node.children !== undefined

    return {
      ...node,
      isDroppable: isFolder,
      children: node.children ? applyDroppableTreeNodes(node.children, lookup) : undefined
    }
  })

export const collectAddedFeatureIds = (
  nodes: TreeNode[],
  lookup: Map<string, FeatureMenuNode>
): Set<number> => {
  const ids = new Set<number>()

  const walk = (items: TreeNode[]) => {
    for (const item of items) {
      const meta = lookup.get(item.id)

      if (meta?.type === MenuNodeType.Feature && meta.featureId !== null) {
        ids.add(meta.featureId)
      }

      if (item.children) {
        walk(item.children)
      }
    }
  }

  walk(nodes)

  return ids
}

export const hasDuplicateFolderLabels = (nodes: TreeNode[]): boolean => {
  const seenLabels = new Set<string>()

  const walk = (items: TreeNode[]): boolean => {
    for (const item of items) {
      if (item.children === undefined) {
        continue
      }

      if (seenLabels.has(item.label)) {
        return true
      }

      seenLabels.add(item.label)

      if (walk(item.children)) {
        return true
      }
    }

    return false
  }

  return walk(nodes)
}

export const applySelectableHomeIcons = (
  nodes: TreeNode[],
  lookup: Map<string, FeatureMenuNode>,
  homePageNodeId: string | null
): TreeNode[] => applyHomeIcons(nodes, lookup, homePageNodeId, true)

export const applyStaticHomeIcons = (
  nodes: TreeNode[],
  lookup: Map<string, FeatureMenuNode>,
  homePageNodeId: string | null
): TreeNode[] => applyHomeIcons(nodes, lookup, homePageNodeId, false)

const applyHomeIcons = (
  nodes: TreeNode[],
  lookup: Map<string, FeatureMenuNode>,
  homePageNodeId: string | null,
  selectable: boolean
): TreeNode[] =>
  nodes.map((node) => {
    const isFeature = lookup.get(node.id)?.type === MenuNodeType.Feature
    return {
      ...node,
      children: node.children
        ? applyHomeIcons(node.children, lookup, homePageNodeId, selectable)
        : undefined,
      right: isFeature
        ? selectable
          ? {type: 'iconButton' as const, icon: mdiHomeOutline, toggled: node.id === homePageNodeId}
          : node.id === homePageNodeId
            ? {type: 'iconButton' as const, icon: mdiHome}
            : undefined
        : node.right
    }
  })

export const translateFeatureLabels = (
  nodes: TreeNode[],
  lookup: Map<string, FeatureMenuNode>,
  t: (key: string, options?: {defaultValue: string}) => string
): TreeNode[] =>
  nodes.map((node) => {
    const meta = lookup.get(node.id)
    return {
      ...node,
      label:
        meta?.type === MenuNodeType.Feature
          ? t(`suite.featureName.${meta.name}`, {defaultValue: meta.name})
          : node.label,
      children: node.children ? translateFeatureLabels(node.children, lookup, t) : undefined
    }
  })

export const findNewlyToggledNodeId = (
  prevNodes: TreeNode[],
  nextNodes: TreeNode[]
): string | undefined => {
  for (let i = 0; i < nextNodes.length; i++) {
    const prev = prevNodes[i]
    const next = nextNodes[i]
    if (next.right?.toggled && !prev?.right?.toggled) return next.id
    if (next.children && prev?.children) {
      const found = findNewlyToggledNodeId(prev.children, next.children)
      if (found) return found
    }
  }
  return undefined
}
